import { useState, useCallback } from 'react';
import log from '../utilities/log';
import mediator from '../utilities/mediator';
import i18n from '../utilities/i18n';
import { DOWNLOAD_CONFIG, DownloadStatus } from '../constants';

export interface DownloadParams {
  studyKey: string;
  studyName: string;
  subjectKeys?: string[];
  includeAudit?: boolean;
}

export interface DownloadState {
  status: DownloadStatus;
  progress?: number;
  downloadUrl?: string;
  filename?: string;
  errorMessage?: string;
}

/**
 * Hook for exporting study data with progress tracking and download handling
 */
export function useStudyDownload() {
  const [downloadState, setDownloadState] = useState<DownloadState>({
    status: DownloadStatus.IDLE,
  });

  const downloadStudyData = useCallback(
    async (params: DownloadParams) => {
      // Prevent concurrent downloads
      if (
        downloadState.status === DownloadStatus.PREPARING ||
        downloadState.status === DownloadStatus.PROCESSING
      ) {
        const error = new Error(
          i18n.t('Modal_Download.error_already_in_progress'),
        );

        log.warn('Download already in progress, rejecting new request');
        throw error;
      }

      try {
        // Set initial status
        setDownloadState({
          status: DownloadStatus.PREPARING,
          progress: 0,
        });

        // Set processing status
        setDownloadState((prev) => ({
          ...prev,
          status: DownloadStatus.PROCESSING,
        }));

        // Use the service for actual download
        const blob = await mediator.request<Blob>('study:downloadData', {
          studyKey: params.studyKey,
          subjectKeys: params.subjectKeys,
          includeAudit: params.includeAudit,
          onProgress: (progressPercent: number) => {
            setDownloadState((prev) => ({
              ...prev,
              progress: progressPercent,
            }));
          },
        });

        // Create download URL and update state
        const url = window.URL.createObjectURL(blob);
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const filename = `${params.studyName}_${timestamp}${DOWNLOAD_CONFIG.FILE_EXTENSION}`;

        setDownloadState((prev) => ({
          ...prev,
          status: DownloadStatus.COMPLETED,
          downloadUrl: url,
          filename,
        }));

        return { success: true, filename, downloadUrl: url };
      } catch (error) {
        log.error('download failed:', error);

        setDownloadState((prev) => ({
          ...prev,
          status: DownloadStatus.FAILED,
          errorMessage:
            error instanceof Error
              ? error.message
              : i18n.t('Modal_Download.error_generic_failed'),
        }));
        throw error;
      }
    },
    [downloadState.status],
  );

  const resetDownload = useCallback(() => {
    // Clean up download URL if it exists
    if (downloadState.downloadUrl) {
      window.URL.revokeObjectURL(downloadState.downloadUrl);
    }

    setDownloadState({
      status: DownloadStatus.IDLE,
    });
  }, [downloadState.downloadUrl]);

  return {
    downloadStudyData,
    resetDownload,
    isPending:
      downloadState.status === DownloadStatus.PREPARING ||
      downloadState.status === DownloadStatus.PROCESSING,
    downloadState,
  };
}

export default useStudyDownload;
